import { Close, InfoOutlined } from '@mui/icons-material'
import { Alert, Button, Collapse, IconButton, Tooltip } from '@mui/material'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'

import { DEFAULT_DAYS_LOOKBACK } from '@/constants'
import { DashboardIntervalStatFragment } from '@/graphql'
import { DataCoverageInfo } from '@/hooks/useDataCoverage'
import useT from '@/i18n/useT'
import { filterDataPointsWithMeaningfulData } from '@/utils/dataCoverage.helpers'

interface DataCoverageAlertProps {
  dataCoverage: DataCoverageInfo
  onAdjustDateRange?: (startDate: string, endDate: string) => void
  chartData?: DashboardIntervalStatFragment[]
  currentStartDate?: string
  currentEndDate?: string
}

export const DataCoverageAlert = ({
  dataCoverage,
  onAdjustDateRange,
  chartData,
  currentStartDate,
  currentEndDate,
}: DataCoverageAlertProps) => {
  const { t } = useT()
  const [isOpen, setIsOpen] = useState(true)

  const { start: suggestedStartDate, end: suggestedEndDate } = useMemo(() => {
    const today = dayjs()

    const startDay = dayjs(currentStartDate)
    const endDay = dayjs(currentEndDate)

    const defaultEnd = today.format('YYYY-MM-DD')
    const defaultStart = today
      .subtract(DEFAULT_DAYS_LOOKBACK, 'day')
      .format('YYYY-MM-DD')

    if (
      !chartData ||
      !currentStartDate ||
      !currentEndDate ||
      !startDay.isValid() ||
      !endDay.isValid()
    ) {
      return { start: defaultStart, end: defaultEnd }
    }

    if (currentEndDate === defaultEnd) {
      return { start: defaultStart, end: defaultEnd }
    }

    const dataPointsWithData = filterDataPointsWithMeaningfulData(chartData)

    if (dataPointsWithData.length === 0) {
      return { start: defaultStart, end: defaultEnd }
    }

    const dataPointDates = dataPointsWithData.map(dp => dayjs(dp.date))
    const earliestDataDate = dataPointDates.reduce((earliest, current) =>
      current.isBefore(earliest) ? current : earliest,
    )
    const latestDataDate = dataPointDates.reduce((latest, current) =>
      current.isAfter(latest) ? current : latest,
    )

    const startDistanceToData = Math.min(
      ...dataPointDates.map(date => Math.abs(date.diff(startDay, 'day'))),
    )
    const endDistanceToData = Math.min(
      ...dataPointDates.map(date => Math.abs(date.diff(endDay, 'day'))),
    )

    if (startDistanceToData <= endDistanceToData) {
      return {
        start: currentStartDate,
        end: latestDataDate.format('YYYY-MM-DD'),
      }
    } else {
      return {
        start: earliestDataDate.format('YYYY-MM-DD'),
        end: currentEndDate,
      }
    }
  }, [chartData, currentStartDate, currentEndDate])
  const tooltipText = `${suggestedStartDate} to ${suggestedEndDate}`

  const handleAdjustDateRange = () => {
    if (onAdjustDateRange) {
      onAdjustDateRange(suggestedStartDate, suggestedEndDate)
    }
    setIsOpen(false)
  }

  if (!dataCoverage.isLowCoverage || !isOpen) {
    return null
  }

  return (
    <Collapse in={isOpen}>
      <Alert
        severity="warning"
        icon={<InfoOutlined />}
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onAdjustDateRange && (
              <Tooltip title={tooltipText} arrow placement="top">
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleAdjustDateRange}
                >
                  {t('dataCoverage.adjustDateRange', 'Adjust date range')}
                </Button>
              </Tooltip>
            )}
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setIsOpen(false)}
            >
              <Close fontSize="inherit" />
            </IconButton>
          </div>
        }
        sx={{ mt: 2, mb: 2 }}
      >
        {t('dataCoverage.limitedDataMessage', {
          coverageMessage: dataCoverage.coverageMessage,
          coveragePercentage: dataCoverage.coveragePercentage,
        })}
      </Alert>
    </Collapse>
  )
}
